import { useEffect, useMemo, useState } from "react";
import { Briefcase, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import collaboratorApi from "../../../lib/collaboratorApi";
import { Card, EmptyState, PageHeader, Pill, TabSkeleton } from "./ui";

const DEFAULT_TASK = {
  title: "",
  assignee_collaborator_id: "",
  assignee_collaborator_ids: [],
};

function isWeddingPlanner(role) {
  return (role || "").toLowerCase().includes("wedding planner");
}

export default function MissionBoardTab() {
  const [session, setSession] = useState(null);
  const [missions, setMissions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTask, setNewTask] = useState(DEFAULT_TASK);
  const [error, setError] = useState("");
  const [activeColumn, setActiveColumn] = useState("todo");

  useEffect(() => {
    let ignore = false;

    async function loadInitial() {
      try {
        const [meRes, tasksRes, assigneesRes] = await Promise.all([
          collaboratorApi.get("/collaborator-auth/me"),
          collaboratorApi.get("/collaborator-portal/tasks"),
          collaboratorApi.get("/collaborator-portal/mission-assignees"),
        ]);
        if (ignore) return;
        setSession(meRes.data);
        setMissions(tasksRes.data || []);
        setTeamMembers(assigneesRes.data || []);
      } catch (loadError) {
        if (!ignore) setError("Impossible de charger les missions collaborateur.");
      }
    }

    async function refreshTasks() {
      try {
        const tasksRes = await collaboratorApi.get("/collaborator-portal/tasks");
        if (!ignore) setMissions(tasksRes.data || []);
      } catch (err) {
        console.error("Erreur actualisation missions", err);
      }
    }

    loadInitial();
    const intervalId = setInterval(refreshTasks, 10000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, []);

  const planner = isWeddingPlanner(session?.role);
  const visibleMissions = useMemo(() => missions, [missions]);

  const todoMissions = visibleMissions.filter((mission) => (mission.status || "todo") === "todo");
  const inProgressMissions = visibleMissions.filter((mission) => mission.status === "in-progress");
  const validationMissions = visibleMissions.filter((mission) => mission.status === "validation_pending");
  const doneMissions = visibleMissions.filter((mission) => mission.status === "done");

  async function moveTask(id, newStatus) {
    try {
      const response = await collaboratorApi.patch(`/collaborator-portal/tasks/${id}`, {
        status: newStatus,
        completed: newStatus === "done" || newStatus === "validation_pending",
      });
      setMissions((prev) => prev.map((mission) => (mission.id === response.data.id ? response.data : mission)));
    } catch (moveError) {
      toast.error(moveError.response?.data?.detail || "Impossible de mettre à jour cette mission.");
    }
  }

  async function handleCreateTask() {
    if (!newTask.title.trim()) return;
    try {
      const response = await collaboratorApi.post("/collaborator-portal/tasks", {
        title: newTask.title.trim(),
        assignee_collaborator_id: newTask.assignee_collaborator_id || null,
        assignee_collaborator_ids: newTask.assignee_collaborator_ids || [],
      });
      setMissions((prev) => [...prev, response.data]);
      setNewTask(DEFAULT_TASK);
      setShowNewTask(false);
      toast.success("Mission créée.");
    } catch (createError) {
      toast.error(createError.response?.data?.detail || "Impossible de créer cette mission.");
    }
  }

  async function handleDeleteTask(mission) {
    if (!window.confirm(`Supprimer définitivement la tâche « ${mission.title} » ?`)) return;
    try {
      await collaboratorApi.delete(`/collaborator-portal/tasks/${mission.id}`);
      setMissions((prev) => prev.filter((item) => item.id !== mission.id));
      toast.success("Tâche supprimée.");
    } catch (deleteError) {
      toast.error(deleteError.response?.data?.detail || "Impossible de supprimer cette tâche.");
    }
  }

  if (error) {
    return <div role="alert" className="w-full rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">{error}</div>;
  }

  if (!session) return <TabSkeleton />;

  const columns = [
    { key: "todo", title: "À faire", items: todoMissions, tone: "neutral" },
    { key: "in-progress", title: "En cours", items: inProgressMissions, tone: "info" },
    { key: "validation_pending", title: "À valider", items: validationMissions, tone: "warning" },
    { key: "done", title: "Terminé", items: doneMissions, tone: "success" },
  ];
  const PRIORITY_TONE = { Haute: "danger", Moyenne: "warning", Basse: "neutral" };
  const action = "rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors";
  const field = "w-full rounded-xl border border-[#e2d9c8] bg-[#fbf8f2] px-3.5 py-2.5 text-sm text-[#211d19] outline-none placeholder:text-[#8b7f70] focus:border-[#5f7d68] focus:bg-white";

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        eyebrow="Coordination"
        title={planner ? "Toutes les missions" : "Mes missions"}
        subtitle={planner
          ? "Pilotez les tâches de coordination du mariage en temps réel."
          : "Gérez vos tâches, déléguez à l’équipe et envoyez si besoin une requête aux mariés."}
        actions={(
          <button type="button" onClick={() => setShowNewTask((open) => !open)} className="flex items-center gap-2 rounded-xl bg-[#211d19] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle tâche
          </button>
        )}
      />

      {showNewTask ? (
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-xs font-semibold text-[#4a4239]">Titre de la mission
              <input type="text" value={newTask.title} onChange={(event) => setNewTask((prev) => ({ ...prev, title: event.target.value }))} className={`${field} mt-1`} placeholder="Ex : Confirmer le nombre d'invités au traiteur" />
            </label>
            <fieldset>
              <legend className="text-xs font-semibold text-[#4a4239]">Assigner à l’équipe</legend>
              <div className="mt-1 max-h-36 overflow-y-auto rounded-xl border border-[#e2d9c8] bg-[#fbf8f2] p-3 text-[#211d19]">
                {teamMembers.map((member) => {
                  const checked = (newTask.assignee_collaborator_ids || []).includes(member.id);
                  return (
                    <label key={member.id} className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                      <input type="checkbox" className="h-4 w-4 accent-[#5f7d68]" checked={checked} onChange={() => setNewTask((prev) => ({ ...prev, assignee_collaborator_id: "", assignee_collaborator_ids: checked ? prev.assignee_collaborator_ids.filter((id) => id !== member.id) : [...prev.assignee_collaborator_ids, member.id] }))} />
                      {member.id === session?.id ? "Moi" : member.name} — {member.role}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-[#6a6055]">Sans sélection : demande aux mariés.</p>
            </fieldset>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowNewTask(false)} className="rounded-xl border border-[#e2d9c8] px-4 py-2.5 text-sm font-semibold text-[#4a4239] hover:bg-[#f6f1e8]">Annuler</button>
            <button type="button" onClick={handleCreateTask} disabled={!newTask.title.trim()} className="rounded-xl bg-[#5f7d68] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#4f6a58] disabled:opacity-50">Ajouter</button>
          </div>
        </Card>
      ) : null}

      {visibleMissions.length === 0 ? (
        <EmptyState icon={Briefcase} title="Aucune mission" text="Les missions qui vous sont confiées, ou que vous créez, apparaissent ici." />
      ) : null}

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:hidden" role="tablist" aria-label="Statut des missions">
        {columns.map((column) => (
          <button key={column.key} type="button" role="tab" aria-selected={activeColumn === column.key} onClick={() => setActiveColumn(column.key)} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${activeColumn === column.key ? "border-[#211d19] bg-[#211d19] text-white" : "border-[#e2d9c8] bg-white text-[#5b5247]"}`}>
            {column.title} <span className={activeColumn === column.key ? "text-white/70" : "text-[#8b7f70]"}>{column.items.length}</span>
          </button>
        ))}
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-4">
        {columns.map((column) => (
          <section key={column.key} aria-label={column.title} className={`${activeColumn === column.key ? "block" : "hidden"} space-y-3 lg:block`}>
            <div className="hidden items-center justify-between border-b border-[#e9e1d4] pb-3 lg:flex">
              <h2 className="font-serif text-lg text-[#211d19]">{column.title}</h2>
              <Pill tone={column.tone}>{column.items.length}</Pill>
            </div>

            {column.items.map((mission) => {
              const isMine = mission.assignee_collaborator_id === session?.id;
              const isForCouple = !mission.assignee_collaborator_id;
              return (
                <article key={mission.id} className={`rounded-2xl border bg-white p-4 shadow-sm ${column.key === "validation_pending" ? "border-amber-200" : "border-[#e9e1d4]"} ${column.key === "done" ? "opacity-70" : ""}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#5f7d68]">
                    {(mission.assignees || []).length ? mission.assignees.map((assignee) => assignee.name || "Collaborateur").join(", ") : "Les mariés"}
                  </p>
                  <h3 className="mt-1 break-words font-semibold text-[#211d19]">{mission.title}</h3>
                  <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#5b5247]">
                    <span>{mission.category || "Autre"}</span>
                    <Pill tone={PRIORITY_TONE[mission.priority] || "neutral"}>{mission.priority || "Moyenne"}</Pill>
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#f0e9dc] pt-3">
                    {column.key === "todo" ? (
                      <button type="button" onClick={() => moveTask(mission.id, "in-progress")} className={`${action} bg-[#eef3ec] text-[#3f5a48] hover:bg-[#e3ece0]`}>Commencer</button>
                    ) : null}
                    {column.key === "in-progress" && (planner || isMine) ? (
                      <button type="button" onClick={() => moveTask(mission.id, "done")} className={`${action} bg-[#eef3ec] text-[#3f5a48] hover:bg-[#e3ece0]`}>Terminer</button>
                    ) : null}
                    {column.key === "in-progress" && !planner && isForCouple ? (
                      <button type="button" onClick={() => moveTask(mission.id, "validation_pending")} className={`${action} bg-amber-50 text-amber-800 hover:bg-amber-100`}>Demander la validation des mariés</button>
                    ) : null}
                    {column.key === "validation_pending" && planner ? (
                      <button type="button" onClick={() => moveTask(mission.id, "done")} className={`${action} bg-emerald-50 text-emerald-800 hover:bg-emerald-100`}>Valider</button>
                    ) : null}
                    {(planner || mission.created_by_collaborator_id === session?.id) ? (
                      <button type="button" onClick={() => handleDeleteTask(mission)} className={`${action} ml-auto inline-flex items-center gap-1 text-rose-700 hover:bg-rose-50`}>
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        Supprimer
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
            {column.items.length === 0 ? <p className="rounded-2xl border border-dashed border-[#e2d9c8] px-4 py-6 text-center text-sm italic text-[#6a6055]">Aucune mission ici.</p> : null}
          </section>
        ))}
      </div>
    </div>
  );
}
